/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useState } from 'react'
import { Formik, FormikHelpers } from 'formik'
import { Button, FormGroup, Form } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { Task } from '../../services/api/types'
import { getTask, approveTask, rejectTask, reassignTask, getMyTasks } from '../../services/api/tasks'
import { getRejectionReasons } from '../../services/api/api.settings'
import { usersApiService } from '../../services/api/users'
import { eosApiService } from '../../services/api/api.eos'
import { toast } from '../../services/toast'
import { useModal } from '../../contexts/ModalContext'
import { WorkflowStepType, WorkflowType } from '../../constants/workflow'
import { FsiType } from '../../constants/fsi'
import { TaskType, TaskStatus } from '../../constants/task'
import AfterRejectionModal from '../../screens/tasks/Actions/after-rejection/AfterRejectionModal'
import AckCompletedDialog, { type AckCompletedDialogTask } from './AckCompletedDialog'
import { Role, USER_ROLE } from '../../constants/user'
import { useAuthService } from '../../hooks/useAuthService'
import { getPdfUrlInShowFile } from '../../services/api/files'
import { validateFileFactory } from '../../services/factory/validateFileFactory'
import { Select } from '../Select/Select'
import TextArea from '@mdsol/lego/lib/TextArea'
import RcmEsignatureModal from '../esignature/RcmEsignatureModal'
import { useEsignature } from '../../hooks/useEsignature'
import { useNavigate, useSearchParams } from 'react-router'
import WorkflowDetailsModal from '../modals/WorkflowDetailsModal'
import ParticipatingFsisModal from '../modals/ParticipatingFsisModal'
import TextLinkButton from '../shared/TextLinkButton'
import TaskService from '~/services/task.service'

// Constants
export const TASK_ACTIONS = {
  taskComplete: 'taskComplete',
  taskReject: 'taskReject',
  taskReassign: 'taskReassign'
} as const

type TaskActionType = (typeof TASK_ACTIONS)[keyof typeof TASK_ACTIONS]

interface TaskCompleteProps {
  task: Task
  isModal?: boolean
  type?: TaskActionType
  onFinished?: () => void
  onApprove?: () => void
  onCancel?: () => void
  params?: Record<string, any>
  lastPageSeen?: boolean
  openedFsiId?: number
  beforeAngularServiceCall?: () => void
  onReassignApprove?: () => void
}

interface FormValues {
  comment: string
  rejectReasonId: number | null
  reassignTo: number | null
}

interface SelectOption {
  value: number
  label: string
}

interface TaskFile {
  id: number
  name: string
  type: string
  versionId: number
  minorVersionId: number
  mustBeSeen?: boolean
  viewed?: boolean
}

/**
 * Task completion component - Handles approve/reject/reassign actions
 * Supports EOS workflows, rejection reasons, user reassignment, e-signature
 * Migrated from lego/page/tasks/complete/TaskComplete.jsx
 */
function TaskComplete({
  task,
  isModal = false,
  type = TASK_ACTIONS.taskComplete,
  onFinished,
  onApprove,
  onCancel,
  params,
  lastPageSeen = false,
  openedFsiId,
  beforeAngularServiceCall,
  onReassignApprove
}: TaskCompleteProps) {
  const [searchParams, setSearchParams] = useSearchParams()
  const { t, i18n } = useTranslation()
  const auth = useAuthService()
  const { showModal } = useModal()
  const { signAction, showDsoModal, esignMessage, handleDsoSubmit, handleDsoClose } = useEsignature()

  const [action] = useState(1) // Action type - always defaults to 1 (never reset in legacy)
  const [taskDetails, setTaskDetails] = useState<Task | null>(null)
  const [taskFile, setTaskFile] = useState<TaskFile | null>(null)
  const [isSimplified, setIsSimplified] = useState(false)
  const [isEosDownload, setIsEosDownload] = useState(false)
  const [isEosAcknowledge, setIsEosAcknowledge] = useState(false)
  const [rejectionReasons, setRejectionReasons] = useState<SelectOption[]>([])
  const [usersOptions, setUsersOptions] = useState<SelectOption[]>([])
  const [isOriginalFileDownloaded, setIsOriginalFileDownloaded] = useState(false)
  const [userRole, setUserRole] = useState<string>('')
  const navigate = useNavigate()
  const validateFile = validateFileFactory()

  const REFRESH_REPEAT_COUNT = 10
  const REFRESH_TIMEOUT = 1500

  /**
   * Postprocess task to add PDF viewing and download capabilities.
   * Extends the task with pdfInfo, pdfSrc, thumbnailId, and downloadFn for AckCompletedDialog.
   */
  const postprocessTask = (taskData: Task): AckCompletedDialogTask => {
    const file = taskData.files?.[0]?.draftFsi || taskData.files?.[0]?.targetFsi
    if (!file) {
      return {
        ...taskData,
        pdfInfo: undefined,
        pdfSrc: undefined,
        thumbnailId: undefined,
        downloadFn: () => Promise.resolve()
      }
    }

    return {
      ...taskData,
      pdfInfo: {
        fsiId: file.id
      },
      pdfSrc: {
        service: () => getPdfUrlInShowFile(file.id, -1, 0, null)
      },
      downloadFn: () => validateFile.downloadPdf(file.id, true)
    }
  }

  /**
   * Recursively refreshes task data until targetFsi is available or max attempts reached.
   * Legacy EOS tasks may take time to generate PDF, so we retry multiple times.
   */
  const taskRefreshRecursive = (taskId: string): Promise<Task> => {
    return new Promise((resolve, reject) => {
      const tryRefresh = (counter: number) => {
        getTask(taskId)
          .then((refreshedTask) => {
            // Check if task has files with targetFsi (PDF is ready)
            if (refreshedTask.files?.[0]?.targetFsi) {
              resolve(refreshedTask)
            } else if (counter >= REFRESH_REPEAT_COUNT) {
              // Max retries reached, resolve with what we have
              resolve(refreshedTask)
            } else {
              // Wait and retry
              setTimeout(() => {
                tryRefresh(counter + 1)
              }, REFRESH_TIMEOUT)
            }
          })
          .catch(reject)
      }
      tryRefresh(1)
    })
  }

  // Load rejection reasons for reject action
  useEffect(() => {
    if (type === TASK_ACTIONS.taskReject) {
      getRejectionReasons(i18n.language || 'en-us')
        .then((data: any) => {
          const reasons = [
            ...data.elements.map((r: any) => ({ value: r.id, label: r.text })),
            { value: 0, label: t('CUSTOM...') }
          ]
          setRejectionReasons(reasons)
        })
        .catch((error) => {
          console.error('Failed to load rejection reasons:', error)
        })
    }
  }, [type, i18n.language, t])

  // Set simplified mode from params
  useEffect(() => {
    setIsSimplified(!!params?.isSimplified)
  }, [params])

  // Load task details and setup state
  useEffect(() => {
    const setTaskCompleteState = (data: Task) => {
      setTaskDetails(data)
      setIsEosDownload(data.workflowStepType === WorkflowStepType.DOWNLOAD_EOS)
      setIsEosAcknowledge(data.workflowStepType === WorkflowStepType.ACKNOWLEDGE_EOS)

      // Extract first file
      if (data.files && data.files.length === 1) {
        const file = data.files[0].draftFsi || data.files[0].targetFsi || data.files[0].sourceFsi
        setTaskFile(file || null)
      }

      // Load users for reassignment
      if (type === TASK_ACTIONS.taskReassign) {
        // Check if this is an EOS workflow (using string comparison since constant might not exist)
        if ((data as any).workflowType === 'EOS_DOWNLOAD_AND_ACKNOWLEDGE') {
          eosApiService
            .getWorkflowUsers((task as any).workflowParentFolderId)
            .then((userData: any[]) => {
              const filteredUsers = userData.filter((user) => user.id !== (task as any).userId)
              setUsersOptions(filteredUsers.map((u) => ({ value: u.id, label: u.name })))
            })
            .catch((error) => {
              console.error('Failed to load EOS workflow users:', error)
            })
        } else {
          usersApiService
            .getAssembledUsersGroups()
            .then((userData: any) => {
              const filteredUsers = userData.users.filter((user: any) => user.id !== (task as any).userId)
              setUsersOptions(filteredUsers.map((u: any) => ({ value: u.id, label: u.name })))
            })
            .catch((error) => {
              console.error('Failed to load users:', error)
            })
        }
      }
    }

    if (task) {
      // Load full task details if flags or workflowStepType missing
      if (!(task as any).flags || !task.workflowStepType) {
        getTask(String(task.id || searchParams.get('taskId')))
          .then((data) => {
            setTaskCompleteState(data)
          })
          .catch((error) => {
            console.error('Failed to load task details:', error)
          })
      } else {
        setTaskCompleteState(task)
      }
    }
  }, [task, type, t, searchParams])

  // Setup user role and originalFileDownloaded event listener
  useEffect(() => {
    if (auth.getRole) {
      const role = auth.getRole()
      setUserRole(role || '')
    }

    // Listen for original file downloaded event (for EOS workflows)
    const handleOriginalFileDownloaded = () => {
      setIsOriginalFileDownloaded(true)
    }

    window.addEventListener('originalFileDownloaded', handleOriginalFileDownloaded)

    return () => {
      window.removeEventListener('originalFileDownloaded', handleOriginalFileDownloaded)
    }
  }, [auth])

  // Check if task can be completed based on document viewing requirements
  const isEnabled = (): boolean => {
    if (!taskDetails || !taskDetails.id) return false

    let isDisabled = false

    // Check all files for viewing requirements
    if (taskDetails.files) {
      taskDetails.files.forEach((file) => {
        const taskFile = file.draftFsi || file.targetFsi || file.sourceFsi
        if (taskFile) {
          const mustBeSeen = (taskFile as any).mustBeSeen
          const viewed = (taskFile as any).viewed
          if (mustBeSeen && !viewed && !(openedFsiId === taskFile.id && lastPageSeen)) {
            isDisabled = true
          }
        }
      })
    }

    const file = taskFile

    return (
      (taskDetails as any).taskType === TaskType.USER_REJECTED_TASK ||
      !file ||
      (taskDetails.files && taskDetails.files.length > 1 && !isDisabled) ||
      (taskDetails.files &&
        taskDetails.files.length === 1 &&
        file &&
        ((file.mustBeSeen && (lastPageSeen || file.viewed)) || !file.mustBeSeen)) ||
      isOriginalFileDownloaded
    )
  }

  // Handle task reassignment
  const handleReassign = async (values: FormValues): Promise<void> => {
    if (!values.reassignTo || !taskDetails) return

    // Call beforeAngularServiceCall callback if provided (for modal closing)
    if (isModal && beforeAngularServiceCall) {
      beforeAngularServiceCall()
    }

    // Check if eSignature is required
    const needsESignature = (taskDetails as any).flags?.reassignESignatureRequired

    if (needsESignature) {
      // Use eSignature hook - handles DSO modal, SSO popup, or OKTA flow
      try {
        const validation = await signAction('ADMIN_ACTION', 'ADMIN_ACTION_TASK_REASSIGN')
        // After successful eSignature, call API with validation
        await reassignTaskInternal(values, validation)
      } catch (error) {
        // User cancelled eSignature - do nothing
        console.log('eSignature cancelled')
      }
    } else {
      // Call API directly without eSignature
      await reassignTaskInternal(values)
    }
  }

  /**
   * Internal reassignment function - handles API call with or without eSignature
   * This function is called either directly (no eSign) or after eSign completion
   */
  const reassignTaskInternal = async (
    values: FormValues,
    validation?: { username?: string; password?: string; relayState?: string; result?: any }
  ): Promise<void> => {
    if (!values.reassignTo || !taskDetails) return

    try {
      // For SSO/OKTA, validation comes as {relayState, result}
      // For DSO, validation comes as {username?, password}
      const esignature = validation?.relayState ? validation : validation

      // Call API with validation parameter (4th argument)
      await reassignTask(String(taskDetails.id), values.reassignTo, values.comment, esignature)

      window.dispatchEvent(new CustomEvent('updateTasks'))

      // Call callbacks in sequence - matching legacy order: onFinished -> toast -> onReassignApprove
      if (onFinished) {
        onFinished()
      }

      // Show success message
      toast.success(t('TASKS_REASSIGN_SUCCESS_MSG'))

      if (onReassignApprove) {
        onReassignApprove()
      }
    } catch (error: any) {
      // Match legacy error handling: access data.data.message
      const errorMessage = error.body?.message || error.message || t('TASK_REASSIGN_FAILED')
      toast.error(errorMessage)
      throw error // Re-throw to allow caller to handle if needed
    }
  }

  // Handle task rejection
  const handleReject = async (values: FormValues): Promise<void> => {
    if (!taskDetails) return

    // Call beforeAngularServiceCall callback if provided (for modal closing)
    if (isModal && beforeAngularServiceCall) {
      beforeAngularServiceCall()
    }

    // Check if eSignature is required
    const needsESignature = (taskDetails as any).flags?.rejectESignatureRequired

    if (needsESignature) {
      // Use eSignature hook - handles DSO modal, SSO popup, or OKTA flow
      try {
        const validation = await signAction('ADMIN_ACTION', 'ADMIN_ACTION_TASK_REJECTION')
        // After successful eSignature, call API with validation
        await rejectTaskInternal(values, validation)
      } catch (error) {
        // User cancelled eSignature - do nothing
        console.log('eSignature cancelled')
      }
    } else {
      // Call API directly without eSignature
      await rejectTaskInternal(values)
    }
    // Note: onFinished is called inside rejectTaskInternal after API success
    // Do NOT call it here - that would cause duplicate calls or calls on eSign cancel
  }

  /**
   * Internal rejection function - handles API call with or without eSignature
   * This function is called either directly (no eSign) or after eSign completion
   */
  const rejectTaskInternal = async (
    values: FormValues,
    validation?: { username?: string; password?: string; relayState?: string; result?: any }
  ): Promise<void> => {
    if (!taskDetails) return

    try {
      const rejectionReasonData = values.rejectReasonId !== null ? { id: values.rejectReasonId } : undefined

      // For SSO/OKTA, validation comes as {relayState, result}
      // For DSO, validation comes as {username?, password}
      const esignature = validation?.relayState ? validation : validation

      await rejectTask(String(taskDetails.id), values.comment, values.comment, rejectionReasonData?.id, esignature)

      // Call onApprove immediately (navigates back to task list) - matching legacy behavior
      // The modal will stay open as a portal even after navigation
      if (onApprove) {
        onApprove()
      }

      toast.success(t('TASKS_REJECT_SUCCESS_MSG'))
      window.dispatchEvent(new CustomEvent('updateTasks'))

      const afterRejectionWorkflowTypes: string[] = [
        WorkflowStepType.APPROVE,
        WorkflowStepType.APPROVE_MULTIFILE,
        WorkflowStepType.APPROVE_MIGRATION,
        WorkflowStepType.APPROVE_EOS,
        WorkflowStepType.REVIEW,
        WorkflowStepType.REVIEW_MULTIFILE,
        WorkflowStepType.REVIEW_EOS,
        WorkflowStepType.REVISE_EDIT,
        WorkflowStepType.REVISE_EDIT_MULTIFILE,
        WorkflowStepType.REVISE_EDIT_MIGRATION,
        WorkflowStepType.REVISE_EDIT_EOS
      ]
      const shouldShowAfterRejectionModal =
        taskDetails.workflowId && afterRejectionWorkflowTypes.includes(taskDetails.workflowStepType as string)

      if (shouldShowAfterRejectionModal) {
        try {
          const data = await getMyTasks({
            taskStatus: TaskStatus.OPEN,
            workflowId: taskDetails.workflowId
          })
          const tasksList = data?.elements || []
          const targetTask = tasksList.find((t: Task) => t.taskType === TaskType.USER_REJECTED_TASK)

          if (targetTask && ((targetTask as any).flags?.canRevert || (targetTask as any).canCancelWorkflow)) {
            // Show AfterRejectionModal
            // When modal closes, call onFinished to check for next task
            showModal(
              <AfterRejectionModal
                origTask={taskDetails}
                targetTask={targetTask}
                onClose={() => {
                  // Call onFinished after modal closes (matches legacy: checkNextTask after modal)
                  if (onFinished) {
                    onFinished()
                  }
                }}
              />
            )
            return // Exit early - onFinished will be called from modal's onClose
          }
        } catch (err) {
          // If getMyTasks fails, just continue with normal flow
          console.error('Failed to check for after-rejection task:', err)
        }
      }

      // If AfterRejectionModal was not shown, call onFinished now
      if (onFinished) {
        onFinished()
      }
    } catch (error: any) {
      toast.error(error.message || t('TASK_REJECTION_FAILED'))
      throw error // Re-throw to allow caller to handle if needed
    }
  }

  // Handle task approval
  const handleApprove = async (values: FormValues): Promise<void> => {
    if (!isEnabled() && !isEosDownload) {
      const message =
        taskDetails?.files && taskDetails.files.length > 1
          ? 'WF_COMPLETE_TASK_DISABLE_MSG_MULTI'
          : 'WF_COMPLETE_TASK_DISABLE_MSG_SINGLE'
      toast.error(t(message))
      return
    }

    if (!taskDetails) return

    // Call beforeAngularServiceCall callback if provided (for modal closing)
    if (isModal && beforeAngularServiceCall) {
      beforeAngularServiceCall()
    }

    // Special handling for EOS download - download file first
    if (isEosDownload) {
      if (!taskDetails?.eosDownloadMediaId) {
        toast.info(t('BROWSER_DOWNLOAD_IS_PREPARING'))
        return
      }

      // Download the original file before approving
      await validateFile.downloadOriginal(taskDetails?.eosDownloadMediaId)

      // Wait for download to complete before approving
      await new Promise((resolve) => setTimeout(resolve, 1000))
    }

    // Check if this is a revert action (same logic as TaskService)
    // Revert happens when taskType is USER_REJECTED_TASK for certain workflow step types
    const revertWorkflowTypes = [
      WorkflowStepType.REVISE_EDIT,
      WorkflowStepType.REVISE_EDIT_MULTIFILE,
      WorkflowStepType.REVISE_EDIT_MIGRATION,
      WorkflowStepType.APPROVE_FILE_ARTIFACT,
      WorkflowStepType.REVISE_EDIT_FILE_ARTIFACT,
      WorkflowStepType.REVISE_EDIT_EOS,
      WorkflowStepType.REVIEW_FILE_ARTIFACT,
      WorkflowStepType.REVIEW,
      WorkflowStepType.REVIEW_MULTIFILE,
      WorkflowStepType.REVISE_EDIT_RECLASSIFICATION,
      WorkflowStepType.REVIEW_RECLASSIFICATION,
      WorkflowStepType.REVIEW_EOS,
      WorkflowStepType.APPROVE_RECLASSIFICATION,
      WorkflowStepType.APPROVE_MIGRATION,
      WorkflowStepType.APPROVE_MULTIFILE,
      WorkflowStepType.APPROVE,
      WorkflowStepType.APPROVE_EOS
    ]
    const isRevert =
      revertWorkflowTypes.includes(taskDetails.workflowStepType as any) &&
      (taskDetails as any).taskType === TaskType.USER_REJECTED_TASK

    // Check if eSignature is required (matches TaskService logic)
    // For normal approve: check approveESignatureRequired
    // For revert: check revertESignatureRequired
    const flags = (taskDetails as any).flags
    const needsESignature = isRevert ? flags?.revertESignatureRequired : flags?.approveESignatureRequired

    if (needsESignature) {
      // Use eSignature hook - handles DSO modal, SSO popup, or OKTA flow
      try {
        const validation = await signAction('ADMIN_ACTION', 'ADMIN_ACTION_TASK_APPROVE')
        // After successful eSignature, call API with validation
        await approveTaskInternal(values, validation)
      } catch (error) {
        // User cancelled eSignature - do nothing
        console.log('eSignature cancelled')
      }
    } else {
      // Call API directly without eSignature
      await approveTaskInternal(values)
    }
  }

  /**
   * Internal approval function - handles API call with or without eSignature
   * This function is called either directly (no eSign) or after eSign completion
   */
  const approveTaskInternal = async (
    values: FormValues,
    validation?: { username?: string; password?: string; relayState?: string; result?: any }
  ): Promise<void> => {
    if (!taskDetails) return

    try {
      // Check if this is a Power User or Study Admin acknowledgement step (using string comparison)
      const isPowerUserOrStudyAdminAckStep =
        (userRole === 'POWER_USER' || userRole === 'STUDY_ADMIN') &&
        (task.workflowStepType === WorkflowStepType.ACKNOWLEDGE_EOS ||
          task.workflowStepType === WorkflowStepType.APPROVE_EOS)

      // Build approveParams matching legacy behavior
      const approveParams = {
        ...params,
        isSimplified: !!isSimplified,
        action: action,
        esignature: validation?.relayState ? validation : validation
      }

      // Use TaskService.approveTask to preserve all legacy logic
      // Pass empty onApprove callback for Power User ack steps (we handle it differently)
      await TaskService.approveTask(
        String(taskDetails.id),
        '', // msg parameter
        values.comment,
        taskDetails as any,
        approveParams,
        isPowerUserOrStudyAdminAckStep ? () => {} : onApprove
      )

      // Special handling for Power User or Study Admin acknowledgement steps
      if (isPowerUserOrStudyAdminAckStep) {
        try {
          // For ACKNOWLEDGE_EOS, get task immediately
          // For APPROVE_EOS, use recursive refresh (legacy tasks take time)
          const updatedTask =
            task.workflowStepType === WorkflowStepType.ACKNOWLEDGE_EOS
              ? await getTask(String(task.id))
              : await taskRefreshRecursive(String(task.id))

          // Postprocess the task to add PDF viewing capabilities
          const processedTask = postprocessTask(updatedTask)

          // Show the acknowledgement completed dialog
          showModal(
            <AckCompletedDialog
              task={processedTask}
              onClose={() => {
                if (onApprove) {
                  onApprove()
                }
                if (onFinished) {
                  onFinished()
                }
              }}
            />
          )
        } catch (err) {
          console.error('Task refresh failed:', err)
          toast.error(t('EOS_DASHBOARD_TASK_REGENERATION_FAILED_MSG'))
        }
        return // Exit early - callbacks will be called from modal's onClose
      }

      // For non-Power User tasks, TaskService already shows toast and dispatches events
      // Just call the remaining callbacks
      if (onFinished) {
        onFinished()
      }
    } catch (error: any) {
      toast.error(error.message || t('TASK_APPROVAL_FAILED'))
      throw error // Re-throw to allow caller to handle if needed
    }
  }

  // Form validation
  const validate = (values: FormValues) => {
    const errors: Partial<Record<keyof FormValues, string>> = {}

    // Reject action validations
    if (type === TASK_ACTIONS.taskReject) {
      const flags = (taskDetails as any)?.flags
      const needsComment =
        (flags?.rejectCommentRequired && !flags?.canRejectReason) ||
        (flags?.canRejectReason && values?.rejectReasonId === 0)

      if (needsComment && (!values.comment || values.comment.trim().length === 0)) {
        errors.comment = t('GENERAL_FORM_FIELD_IS_REQUIRED_ERROR')
      }

      if (flags?.canRejectReason && flags?.rejectReasonRequired && values.rejectReasonId === null) {
        errors.rejectReasonId = t('GENERAL_FORM_FIELD_IS_REQUIRED_ERROR')
      }
    }

    // Reassign action validation
    if (type === TASK_ACTIONS.taskReassign && !values.reassignTo) {
      errors.reassignTo = t('GENERAL_FORM_FIELD_IS_REQUIRED_ERROR')
    }

    return errors
  }

  // Form submission handler
  const handleSubmit = async (values: FormValues, { setSubmitting }: FormikHelpers<FormValues>) => {
    try {
      if (type === TASK_ACTIONS.taskReject) {
        await handleReject(values)
      } else if (type === TASK_ACTIONS.taskReassign) {
        await handleReassign(values)
      } else {
        await handleApprove(values)
      }
    } finally {
      setSubmitting(false)
    }
  }

  if (!taskDetails) {
    return (
      <div className={isModal ? 'modal-body' : 'panel-body'}>
        <p className="text-muted">{t('LOADING...')}</p>
      </div>
    )
  }

  const workflowFormattedId = (taskDetails as any).workflowFormattedId || (taskDetails.workflow as any)?.formattedId

  return (
    <>
      <Formik<FormValues>
        initialValues={{
          comment: '',
          rejectReasonId: null,
          reassignTo: null
        }}
        enableReinitialize
        validate={validate}
        onSubmit={handleSubmit}
      >
        {({ values, errors, touched, setFieldValue, handleSubmit, handleReset, isSubmitting, dirty }) => (
          <>
            <div className={isModal ? 'modal-body' : 'panel-body'}>
              {/* Task Type */}
              <FormGroup className="wparag">
                <Form.Label>{t('TYPE')}</Form.Label>
                <p className="whitespaceprewrap">{taskDetails.name}</p>
              </FormGroup>

              {/* Task Description */}
              <FormGroup className="wparag">
                <Form.Label>{t('DESCRIPTION')}</Form.Label>
                <p className="whitespaceprewrap">{taskDetails.workflowDescription}</p>
              </FormGroup>

              {/* Documents/Files */}
              <FormGroup className="wparag">
                <Form.Label>
                  {t(
                    taskDetails.workflowStepType === WorkflowStepType.ACKNOWLEDGE_EOS
                      ? 'EOS_MEDIA_ACKNOWLEDGEMENT_FORM'
                      : isEosDownload
                        ? 'EOS_MEDIA'
                        : 'DOCUMENT(S)'
                  )}
                </Form.Label>
                {isEosDownload ? (
                  <p className="word-break">{(taskDetails as any).eosDownloadMediaName || t('NOT_AVAILABLE')}</p>
                ) : (
                  <p className="word-break">
                    {taskDetails.files && taskDetails.files.length > 1 && (
                      <span>
                        <button
                          type="button"
                          className="btn btn-link pl0"
                          onClick={() => {
                            showModal(
                              <ParticipatingFsisModal
                                wfId={Number(taskDetails.workflowId)}
                                show={true}
                                onClose={() => {}}
                              />,
                              { keepParentModalsOpen: true }
                            )
                          }}
                        >
                          {t('MULTIPLE_DOCS')}
                        </button>
                      </span>
                    )}
                    {taskDetails.files && taskDetails.files.length === 0 && (
                      <span title={(taskDetails as any).filename}>
                        {(taskDetails as any).filename || t('NOT_AVAILABLE')}
                      </span>
                    )}
                    {taskDetails && taskDetails.files && taskDetails.files.length === 1 && taskFile && (
                      <>
                        {taskFile.type !== FsiType.PLACEHOLDER && isModal && (
                          <TextLinkButton
                            action={() =>
                              navigate(
                                `/show-file/${taskFile.id}?action=${type || TASK_ACTIONS.taskComplete}&taskId=${task.id}&version=${taskFile.versionId}&minorVersion=${taskFile.minorVersionId}`
                              )
                            }
                          >
                            {taskFile.name}
                          </TextLinkButton>
                        )}
                        {(taskFile.type === FsiType.PLACEHOLDER || !isModal) && <span>{taskFile.name}</span>}
                      </>
                    )}
                  </p>
                )}
              </FormGroup>

              {/* Workflow/Change Request Number */}
              <FormGroup className="wparag">
                <Form.Label>{t('WORKFLOW_CHANGE_REQUEST_NUMBER')}</Form.Label>
                <p className="whitespaceprewrap">
                  {isModal ? (
                    workflowFormattedId
                  ) : (
                    <button
                      type="button"
                      className="btn btn-link pl0"
                      onClick={() => {
                        showModal(<WorkflowDetailsModal wfId={Number(task.workflowId)} />)
                      }}
                    >
                      {workflowFormattedId}
                    </button>
                  )}
                </p>
              </FormGroup>

              {/* Reassign-specific fields */}
              {type === TASK_ACTIONS.taskReassign && (
                <>
                  {(isEosDownload || isEosAcknowledge) && (
                    <>
                      <FormGroup className="wparag">
                        <Form.Label>{t('EMTF_MD_STUDY')}</Form.Label>
                        <p className="whitespaceprewrap">{(taskDetails as any).studyName || t('NOT_AVAILABLE')}</p>
                      </FormGroup>
                      <FormGroup className="wparag">
                        <Form.Label>{t('EMTF_MD_SITE')}</Form.Label>
                        <p className="whitespaceprewrap">{(taskDetails as any).siteName || t('NOT_AVAILABLE')}</p>
                      </FormGroup>
                    </>
                  )}
                  <FormGroup className="w50p">
                    <Form.Label>{t('REASSIGN_TO')}</Form.Label>
                    <Select
                      placeholder={t('SELECT_USER...')}
                      value={values.reassignTo}
                      options={usersOptions}
                      onChange={(option: any) => {
                        setFieldValue('reassignTo', option ? option.value : null)
                      }}
                      isSearchable
                      className={touched.reassignTo && errors.reassignTo ? 'is-invalid' : ''}
                    />
                    {touched.reassignTo && errors.reassignTo && <div className="text-danger">{errors.reassignTo}</div>}
                  </FormGroup>
                </>
              )}

              {/* Rejection reason */}
              {type === TASK_ACTIONS.taskReject && (taskDetails as any)?.flags?.canRejectReason && (
                <FormGroup className="w50p">
                  <Form.Label>{t('TASK_REJECTION_REASON_LABEL')}</Form.Label>
                  <Select
                    placeholder={t('SELECT_REJECTION_REASON_LABEL')}
                    value={values.rejectReasonId}
                    options={rejectionReasons}
                    onChange={(option: any) => {
                      setFieldValue('rejectReasonId', option ? option.value : null)
                    }}
                    isSearchable
                    className={touched.rejectReasonId && errors.rejectReasonId ? 'is-invalid' : ''}
                  />
                  {touched.rejectReasonId && errors.rejectReasonId && (
                    <div className="text-danger">{errors.rejectReasonId}</div>
                  )}
                </FormGroup>
              )}

              {/* Comment */}
              <FormGroup className="w50p">
                <Form.Label>
                  {t('COMMENT')}{' '}
                  {!(
                    type === TASK_ACTIONS.taskReject &&
                    (((taskDetails as any)?.flags?.rejectCommentRequired &&
                      !(taskDetails as any)?.flags?.canRejectReason) ||
                      ((taskDetails as any)?.flags?.canRejectReason && values?.rejectReasonId === 0))
                  ) && <span className="required">{t('(OPTIONAL)')}</span>}
                </Form.Label>
                <TextArea
                  {...({
                    name: 'comment',
                    className: 'form-control' + (touched.comment && errors.comment ? ' is-invalid' : ''),
                    maxLength: 255,
                    rows: 6,
                    value: values.comment || '',
                    onChange: (e: any) => setFieldValue('comment', e.target.value)
                  } as any)}
                />
                {touched.comment && errors.comment && <div className="text-danger">{errors.comment}</div>}
              </FormGroup>
            </div>

            {/* Footer with action buttons */}
            <div className={(isModal ? 'modal-footer' : 'panel-footer') + ' text-right'}>
              <Button
                variant="link"
                onClick={() => {
                  if (isModal && onCancel) {
                    onCancel()
                  } else {
                    handleReset()
                  }
                }}
                disabled={!isModal && !dirty}
                data-action="cancel"
              >
                {t('CANCEL')}
              </Button>
              <Button
                variant="primary"
                className="text-capitalize"
                onClick={() => handleSubmit()}
                disabled={isSubmitting}
                data-action="confirm"
              >
                {t(
                  type === TASK_ACTIONS.taskReject
                    ? 'REJECT'
                    : type === TASK_ACTIONS.taskReassign
                      ? 'BTN_REASSIGN'
                      : 'APPROVE'
                )}
              </Button>
            </div>
          </>
        )}
      </Formik>

      {/* eSignature Modal - Shown for DSO auth type only */}
      {showDsoModal && (
        <RcmEsignatureModal onSubmit={handleDsoSubmit} onClose={handleDsoClose} esignMessage={esignMessage} />
      )}
    </>
  )
}

export default TaskComplete
